import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, ArrowLeft, HeartHandshake, Sparkles } from "lucide-react";
import AsaLogo from "../components/AsaLogo";
import { TIERS, gbp, hasFullAccess } from "../data";
import { useAsaAuth } from "../AuthContext";
import { startSubscriptionCheckout } from "../payments";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAsaAuth();
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const fullAccess = hasFullAccess(user);

  const essential = TIERS.find((t) => t.id === "essential");
  const full = TIERS.find((t) => t.id === "pro");

  const startFree = () => {
    if (user) { navigate("/asa/dashboard"); return; }
    navigate("/asa/register?tier=essential");
  };

  const getFull = async () => {
    if (!user) { navigate("/asa/register?tier=pro"); return; }
    setLoading(true);
    try {
      await startSubscriptionCheckout({ tier: "pro", billing, kind: "upgrade", email: user.email });
    } catch { setLoading(false); toast.error("Could not start checkout. Please try again."); }
  };

  return (
    <div className="min-h-screen bg-[#f4f8f7]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => navigate(user ? "/asa/dashboard" : "/asa")} className="inline-flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700"><ArrowLeft size={16} /> Back</button>
        <div className="text-center mt-6">
          <AsaLogo size={38} />
          <h1 className="font-head font-bold text-slate-800 text-3xl sm:text-4xl mt-6">Choose how to explore ASAL</h1>
          <p className="text-slate-500 mt-2 max-w-2xl mx-auto">Start free and become aware, or unlock ASAL Pro to track your health over time and prepare for appointments. Every subscription funds the ASA Foundation.</p>
        </div>

        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg w-fit mx-auto mt-6">
          {["monthly", "yearly"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} data-testid={`pricing-billing-${b}`} className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${billing === b ? "bg-white text-[#0f766e] shadow-sm" : "text-slate-500"}`}>{b}{b === "yearly" && " · 2 mo free"}</button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Essential — Free */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid="pricing-essential">
            <span className="text-[11px] font-semibold text-[#0d9488] bg-[#e6f4f2] px-2.5 py-1 rounded-full w-fit">Free forever</span>
            <h2 className="font-head font-bold text-slate-800 text-2xl mt-3">{essential.name}</h2>
            <p className="text-slate-500 text-sm">{essential.tagline}</p>
            <div className="mt-3"><span className="text-3xl font-head font-bold text-[#0d9488]">Free</span></div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {essential.features.map((f) => (<li key={f} className="flex items-start gap-2 text-[14px] text-slate-600"><Check size={17} className="text-[#0d9488] mt-0.5 shrink-0" /> {f}</li>))}
            </ul>
            <button onClick={startFree} data-testid="pricing-start-free" className="w-full mt-6 h-12 rounded-lg border-2 border-[#0d9488] text-[#0f766e] font-semibold hover:bg-[#e6f4f2] transition-colors">{user ? "Go to dashboard" : "Start free"}</button>
          </div>

          {/* ASAL Pro — Paid */}
          <div className="bg-gradient-to-b from-white to-[#f0faf8] rounded-2xl border-2 border-[#0d9488] p-6 flex flex-col relative" data-testid="pricing-full">
            <span className="absolute -top-3 left-6 text-[11px] font-semibold text-white bg-[#0d9488] px-2.5 py-1 rounded-full">Most popular</span>
            <h2 className="font-head font-bold text-slate-800 text-2xl mt-2">{full.name}</h2>
            <p className="text-slate-500 text-sm">{full.tagline}</p>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap"><span className="text-3xl font-head font-bold text-slate-800">{gbp(full[billing])}</span><span className="text-slate-400">/{billing === "yearly" ? "year" : "month"}</span>{billing === "yearly" && <span className="text-[11px] font-bold text-[#22a35a] bg-[#eafaf1] px-2 py-0.5 rounded-full" data-testid="pricing-2mo-badge">2 MONTHS FREE</span>}</div>
            {billing === "yearly" && <p className="text-[12px] text-slate-400 mt-0.5">Just {gbp(full.yearly / 12)}/mo · billed annually</p>}
            <ul className="mt-5 space-y-2.5 flex-1">
              {full.features.map((f) => (<li key={f} className="flex items-start gap-2 text-[14px] text-slate-700"><Check size={17} className="text-[#0d9488] mt-0.5 shrink-0" /> {f}</li>))}
            </ul>
            {fullAccess ? (
              <div className="w-full mt-6 h-12 rounded-lg bg-[#e6f4f2] text-[#0f766e] font-semibold flex items-center justify-center gap-2"><Check size={18} /> You have ASAL Pro</div>
            ) : (
              <button onClick={getFull} disabled={loading} data-testid="pricing-get-full" className="w-full mt-6 h-12 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Sparkles size={18} /> {loading ? "Redirecting…" : "Get ASAL Pro"}</button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#cfe9e5] p-5 mt-6 flex items-start gap-3" data-testid="pricing-charity-note">
          <div className="w-11 h-11 rounded-xl bg-[#e6f4f2] flex items-center justify-center shrink-0"><HeartHandshake size={22} className="text-[#0d9488]" /></div>
          <p className="text-[13px] text-[#0f766e] leading-relaxed"><strong>100% of subscriptions fund the ASA Foundation</strong> — a charity working to bring early autoimmune diagnostics to vulnerable people, including children. Upgrading doesn't just help you; it helps fund earlier diagnosis for those who need it most.</p>
        </div>

        <p className="text-[12px] text-slate-400 text-center mt-6 max-w-2xl mx-auto">ASAL provides health education and awareness. It does not diagnose, predict or calculate an individual's risk of autoimmune disease. Always seek advice from a qualified healthcare professional. © {new Date().getFullYear()} ASA Foundation · Copyright &amp; IP: Asal Shirazi.</p>
      </div>
    </div>
  );
}
